import { DropdownWrapper, SpDropdownItem } from 'components/shared/Dropdown';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { DropdownMenu, DropdownToggle } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import type { ActivityFilter as Activity } from 'state/blotters/common/blotter.model';
import type { AppState } from 'state/state.model';
import { hasUserPermission } from 'state/user';

export const defaultAvailableActivities: readonly Exclude<Activity, 'all'>[] = ['mySelf', 'myTeam'];
const internalActivities: readonly Activity[] = [...defaultAvailableActivities, 'all'];

interface ActivityFilterProps {
  value: Activity;
  activities?: readonly Activity[];
  onSelect: (value: Activity) => void;
}

const activityToIcon: Record<Activity, string> = {
  all: 'public',
  myTeam: 'groups',
  mySelf: 'person',
};

export function ActivityFilter({ value, activities = internalActivities, onSelect }: ActivityFilterProps) {
  const isInternal = useIsInternalUser();
  const hasPermission = useSelector((state: AppState) => hasUserPermission(state, 'ALL_INTERNAL_DESKS_VISIBILITY'));
  const hasAllPermission = isInternal && hasPermission;
  const activitiesByUser = activities.filter(activity => hasAllPermission || activity !== 'all');
  return (
    <DropdownWrapper className="btn-group border-start border-opacity-40" openOnHover>
      <DropdownToggle
        aria-haspopup="listbox"
        variant="link"
        data-e2e="activity-filter"
        className="btn btn-flat-secondary btn-lg btn-icon-start dropdown-toggle pe-2"
      >
        <i className="icon">{activityToIcon[value]}</i>
        <span className="d-none d-lg-inline">
          <FormattedMessage id={`blotter.filter.activity.${value}`} />
        </span>
      </DropdownToggle>
      <DropdownMenu role="listbox">
        {activitiesByUser.map(activity => (
          <SpDropdownItem
            active={activity === value}
            data-e2e={activity}
            key={activity}
            onClick={() => onSelect(activity)}
          >
            <i className="icon me-2">{activityToIcon[activity]}</i>
            <FormattedMessage id={`blotter.filter.activity.${activity}`} />
          </SpDropdownItem>
        ))}
      </DropdownMenu>
    </DropdownWrapper>
  );
}
